// client/src/services/userService.ts
// User management service for AGOC School.
//
// Architecture:
//   READ  (getAllUsers, getTeachers, getStudents) — Firestore, tenant-scoped
//   WRITE (createUser)  — BFF POST /users/create (requires admin privilege;
//                         avoids logging out the current admin session)
//   WRITE (updateRole)  — Firestore for now; should migrate to BFF claim-setting
//                         once platform/backend exposes PATCH /admin/users/:uid/claims
//
// This module replaces the legacy functions that were exported from lib/firebase.ts
// in PilotAcademyAI (getAllUsers, updateUserRole, createUserByAdmin, getTeachers).
// Import from here instead of @/lib/firebase.

import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { apiClient } from '@/lib/apiClient';
import type { SchoolUser, UserRole } from '@/types/schema';

// ── Collection path helpers ─────────────────────────────────────────────────
// Use tenant-scoped paths for all reads/writes.

/** Returns the Firestore collection ref for school users under the given tenant. */
function usersCol(tenantId: string) {
  return collection(db, 'tenants', tenantId, 'users');
}

// ── Types ─────────────────────────────────────────────────────────────────

export interface CreateUserRequest {
  email: string;
  password: string;
  displayName: string;
  role: UserRole;
}

export interface CreateUserResponse {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
}

// ── Reads (Firestore) ──────────────────────────────────────────────────────

/**
 * List all users belonging to the tenant.
 * Replaces legacy `getAllUsers()` from lib/firebase (which used flat /users collection).
 */
export async function getAllUsers(tenantId: string): Promise<SchoolUser[]> {
  const snap = await getDocs(query(usersCol(tenantId), orderBy('displayName')));
  return snap.docs.map((d) => ({ ...d.data(), id: d.id } as SchoolUser));
}

/**
 * List users with teacher or teacher_admin role.
 * Replaces legacy `getTeachers()` from lib/firebase.
 */
export async function getTeachers(tenantId: string): Promise<SchoolUser[]> {
  const [teachersSnap, teacherAdminsSnap] = await Promise.all([
    getDocs(query(usersCol(tenantId), where('role', '==', 'teacher'))),
    getDocs(query(usersCol(tenantId), where('role', '==', 'teacher_admin'))),
  ]);

  const seen = new Set<string>();
  return [...teachersSnap.docs, ...teacherAdminsSnap.docs]
    .filter((d) => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    })
    .map((d) => ({ ...d.data(), id: d.id } as SchoolUser));
}

/**
 * List users with student role.
 */
export async function getStudents(tenantId: string): Promise<SchoolUser[]> {
  const snap = await getDocs(
    query(usersCol(tenantId), where('role', '==', 'student')),
  );
  return snap.docs.map((d) => ({ ...d.data(), id: d.id } as SchoolUser));
}

/** Maps user id → display label for analytics tables (name, email, or fallback). */
export function buildUserDisplayMap(users: SchoolUser[]): Map<string, string> {
  return new Map(
    users.map((u) => [
      u.id,
      u.displayName?.trim() || u.email?.trim() || u.id,
    ]),
  );
}

/** Label for analytics when userId is not in the tenant user list. */
export function unknownUserLabel(userId: string): string {
  const short = userId.length > 10 ? `${userId.slice(0, 8)}…` : userId;
  return `Cuenta eliminada (${short})`;
}

// ── Writes ─────────────────────────────────────────────────────────────────

/**
 * Create a new user via the BFF (POST /users/create).
 *
 * The BFF calls Firebase Admin SDK to create the Auth user and then sets the
 * JWT claims (tenantId + roles) via POST /admin/users/:uid/claims on the platform
 * backend. This avoids the admin's session being disturbed by client-side Auth calls.
 *
 * Replaces legacy `createUserByAdmin()` from lib/firebase.
 */
export async function createUser(req: CreateUserRequest): Promise<CreateUserResponse> {
  return apiClient.post<CreateUserResponse>('/users/create', req);
}

/**
 * Update a user's role in Firestore.
 *
 * 🔲 Ideally this should also update the JWT claim via the platform backend
 *    (PATCH /admin/users/:uid/claims). Until that endpoint is wired, we write
 *    the convenience copy in Firestore; the JWT claim is updated the next time
 *    the admin sets claims via /users/create or a future update-claims endpoint.
 *
 * Replaces legacy `updateUserRole()` from lib/firebase.
 */
export async function updateUserRole(
  tenantId: string,
  uid: string,
  role: UserRole,
): Promise<void> {
  await updateDoc(doc(usersCol(tenantId), uid), { role });
}

/**
 * Permanently delete a user via the BFF (POST /users/delete, userId in body).
 *
 * The BFF removes the Firebase Auth account and Firestore profile, and
 * deactivates any active enrollments. Academic records are preserved.
 *
 * Throws ApiError on failure (403 if unauthorized, 404 if not found, 500 otherwise).
 */
export async function deleteUser(userId: string): Promise<void> {
  await apiClient.post<{ success: boolean; message: string }>('/users/delete', { userId });
}

/**
 * Update arbitrary fields on a user document.
 */
export async function updateUser(
  tenantId: string,
  uid: string,
  data: Partial<SchoolUser>,
): Promise<void> {
  await updateDoc(doc(usersCol(tenantId), uid), data as Record<string, unknown>);
}

/**
 * Fetch the authenticated user's own profile from the BFF (GET /users/me).
 * Creates the Firestore profile automatically if it does not exist yet.
 */
export async function getMyProfile(): Promise<SchoolUser> {
  return apiClient.get<SchoolUser>('/users/me');
}

/** Idempotent: guarantees tenants/{tenantId}/users/{uid} exists after login. */
export async function ensureMyProfile(): Promise<SchoolUser> {
  return getMyProfile();
}

/**
 * Update the authenticated user's own profile via the BFF (PATCH /users/me).
 * Only self-editable fields are accepted; the BFF enforces a strict whitelist.
 */
export async function updateMyProfile(
  data: Partial<Pick<SchoolUser,
    | 'displayName'
    | 'photoURL'
    | 'phoneNumber'
    | 'address'
    | 'documentId'
    | 'licenseNumber'
    | 'licenseType'
    | 'licenseExpiryDate'
    | 'medicalCertExpiry'
    | 'bio'
    | 'specialization'
  >>,
): Promise<void> {
  await apiClient.patch('/users/me', data);
}
